import { useNode } from "@craftjs/core";
import { unwrapResult } from "@reduxjs/toolkit";
import { useRouter } from "next/router";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useAppDispatch } from "src/redux/store";

import SettingsTitleWrapper from "@/components/editor/shared/settings-title-wrapper";
import { CalendarMonthTwoTone } from "@/components/icons/calendar-month-two-tone";
import { CheckmarkCircle } from "@/components/icons/checkmark-circle";
import DataExistsCard from "@/components/import-list/data-exists-card-component";
import EmptyStateCard from "@/components/import-list/empty-state-card-component";
import { ToastContent } from "@/components/toasts/toast-content-component";
import {
  fetchSchedules,
  getGroupedSchedules,
  groupSchedules,
} from "@/features/schedules/schedules-reducer";
import useAnalytics from "@/hooks/use-analytics";
import endpoints from "@/utils/endpoints";
import logger from "@/utils/logger";

const get = endpoints();

export default function ScheduleSettings() {
  const { query } = useRouter();
  const slug = query.slug as string;
  const dispatch = useAppDispatch();
  const { logAnalytics } = useAnalytics();
  const { t } = useTranslation();

  const scheduleList = useSelector(getGroupedSchedules);

  const {
    actions: { setProp },
  } = useNode();

  const handleImportSchedule = React.useCallback(async () => {
    try {
      const response = await dispatch(fetchSchedules(slug));
      const unwrappedSchedule = unwrapResult(response);
      const groupedSchedules = groupSchedules(unwrappedSchedule);
      if (groupedSchedules.length > 0) {
        setProp((props) => (props.schedules = groupedSchedules));
        toast.success(
          <ToastContent
            icon={CheckmarkCircle}
            text={t("preference-panel.successful-data-import")}
          />
        );
        logAnalytics("CANVAS_EDITOR_DATA_IMPORTED", {
          block_type: "schedule",
        });
      }
    } catch (error) {
      logger.error(error, {
        details: "import schedule error",
      });
    }
  }, [dispatch, setProp, slug, logAnalytics, t]);

  if (scheduleList.length === 0) {
    return (
      <EmptyStateCard
        icon={CalendarMonthTwoTone}
        mainEmptyText={t("preference-panel.schedule.no-schedule-available")}
        subEmptyText={t("preference-panel.schedule.add-schedule-details")}
        href={get("HOPIN_DASHBOARD_PATH")(slug, "schedules")}
        buttonText={t("preference-panel.schedule.import-schedule")}
        onImportClick={handleImportSchedule}
      />
    );
  }

  return (
    <SettingsTitleWrapper title={t("preference-panel.schedule.schedule")}>
      <DataExistsCard
        href={get("HOPIN_DASHBOARD_PATH")(slug, "schedules")}
        icon={CalendarMonthTwoTone}
        importText={t("preference-panel.schedule.import-schedule-from-event")}
        buttonText={t("preference-panel.schedule.import-schedule")}
        onClickImport={handleImportSchedule}
      />
    </SettingsTitleWrapper>
  );
}
